import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, BadgeCheck, Loader2, Search, UserCheck } from 'lucide-react'
import { Note } from '@/shared/ui/states'
import type { VoterLookupResponse } from '@/shared/api/types'
import { lookupVoter } from './api'

/**
 * National-ID lookup for the voter form.
 *
 * ## Why the form is gated on this
 *
 * Registration starts with one question — *is this person already known?* — and
 * the rest of the form is only worth showing once it has an answer. Presenting
 * thirty fields first invites the operator to fill them in and only then
 * discover the person is already on the register, or that eCitizen could have
 * supplied half of them correctly. Both are wasted work, and the first is worse
 * than wasted: it is how duplicates get made.
 *
 * So the create dialog opens as a single input and a Search button, and the
 * fields appear afterwards — prefilled if the registry knew the person, empty if
 * it did not.
 *
 * ## Why the search is a click, not a debounce
 *
 * This screen was previously debounced, firing as soon as the ID looked
 * plausible. That is right for a filter and wrong for this: the call goes to a
 * *government identity service*, and a query there is a disclosure of a real
 * person's ID number. A keystroke is not consent to make one — a click is. It
 * also means one lookup per person rather than one per pause in typing, which
 * matters when the operator corrects a digit.
 *
 * Two rules the click does not relax:
 *
 *  1. **Nothing fires until the ID is plausible.** Kenyan national IDs are 7–9
 *     digits. A partial ID is a guaranteed miss and an unnecessary disclosure.
 *     The gate is length, not a timer.
 *  2. **A result is never applied silently.** The prefill is a proposal from a
 *     third party; the operator chooses it, and every field stays editable
 *     afterwards.
 *
 * The request is a POST, deliberately: a national ID must never ride in a URL,
 * where it would land in access logs, history and referer headers.
 */

export const MIN_ID_LENGTH = 7
export const MAX_ID_LENGTH = 9

export interface LookupState {
  status: 'idle' | 'searching' | 'done' | 'error'
  result: VoterLookupResponse | null
  error: string | null
  /** The ID the current result belongs to — never trust a result against another. */
  forId: string | null
}

const IDLE: LookupState = { status: 'idle', result: null, error: null, forId: null }

/** Null when the ID could be one; otherwise why it could not. */
export function idProblem(raw: string): string | null {
  const id = raw.trim()
  if (id === '') return 'Enter a national ID to search.'
  if (!/^\d+$/.test(id)) return 'A national ID is digits only.'
  if (id.length < MIN_ID_LENGTH || id.length > MAX_ID_LENGTH) {
    return `A national ID is ${MIN_ID_LENGTH}–${MAX_ID_LENGTH} digits.`
  }
  return null
}

export function useIdentityLookup() {
  const [state, setState] = useState<LookupState>(IDLE)
  // Guards against a slow earlier response overwriting a later, faster one, and
  // against anything landing after the dialog closes.
  const seq = useRef(0)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const search = useCallback(async (raw: string) => {
    const id = raw.trim()
    if (idProblem(id) !== null) return
    const mine = ++seq.current
    setState({ status: 'searching', result: null, error: null, forId: id })
    try {
      const result = await lookupVoter({ nationalId: id })
      if (alive.current && seq.current === mine) {
        setState({ status: 'done', result, error: null, forId: id })
      }
    } catch (e) {
      // A lookup failure must never block registration — the operator can always
      // type the record in. So this is a note, not a dead end.
      if (alive.current && seq.current === mine) {
        setState({
          status: 'error',
          result: null,
          error: e instanceof Error ? e.message : 'Lookup failed',
          forId: id,
        })
      }
    }
  }, [])

  /** Any edit to the ID invalidates the answer we are holding about a different one. */
  const reset = useCallback(() => {
    seq.current++
    setState(IDLE)
  }, [])

  return { ...state, search, reset }
}

/**
 * The gate: one input, one button, and whatever the search came back with.
 *
 * Every outcome ends in a way forward. An operator who cannot get past this
 * screen cannot register anyone, so "not found", "the service is down" and "this
 * person has no ID on them" all lead somewhere.
 */
export function IdentityGate({
  value, onChange, state, onSearch, onContinueBlank, onRegisterWithoutId,
  onUsePrefill, onOpenExisting,
}: {
  value: string
  onChange: (v: string) => void
  state: LookupState
  onSearch: () => void
  /** Reveal a blank form FOR THIS ID — used after a miss or a service failure. */
  onContinueBlank: () => void
  /** Reveal a blank form with NO id at all. A different act, hence a different handler. */
  onRegisterWithoutId: () => void
  onUsePrefill: (r: VoterLookupResponse) => void
  onOpenExisting: (voterId: string) => void
}) {
  const problem = idProblem(value)
  const searching = state.status === 'searching'
  // A result only speaks for the ID it was fetched against.
  const current = state.forId !== null && state.forId === value.trim() ? state : null
  const r = current?.result ?? null

  return (
    <div className="space-y-3">
      <div>
        {/* The label wraps the input ALONE. Wrapping the button too folds its
            text into the input's accessible name — "National ID * Search" — which
            is what a screen reader then reads out on focus. */}
        <label className="block text-erp-sm font-medium" htmlFor="voter-national-id">
          National ID <span className="text-red-600">*</span>
        </label>
        <div className="mt-1.5 flex gap-2">
          <input
            id="voter-national-id"
            className="erp-input flex-1 min-w-0 font-mono"
            value={value}
            inputMode="numeric"
            autoFocus
            placeholder="e.g. 29359944"
            aria-describedby="voter-national-id-hint"
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !problem && !searching) {
                e.preventDefault()
                onSearch()
              }
            }}
          />
          <button
            type="button"
            className="erp-btn erp-btn-primary shrink-0"
            disabled={problem !== null || searching}
            onClick={onSearch}
          >
            {searching
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Search className="h-3.5 w-3.5" />}
            Search
          </button>
        </div>
        <p id="voter-national-id-hint" className="text-erp-xs text-muted-foreground mt-1.5">
          {value.trim() !== '' && problem
            ? problem
            : 'Checked against the register first. Only if the person is not already ours is eCitizen asked — and the ID is sent in the request body, never in the URL.'}
        </p>
      </div>

      {/* Search moves nothing on screen except this region, and the button
          disables itself mid-flight so focus is on a disabled control. Without
          an announcement a screen-reader user gets silence and then a form that
          appeared from nowhere. */}
      <div aria-live="polite" className="contents">
      {searching && (
        <div className="text-erp-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking the register…
        </div>
      )}

      {current?.status === 'error' && (
        <div className="space-y-2">
          <Note tone="info">
            Could not reach the identity service. Registration is never blocked by a lookup —
            carry on and type the details in.
          </Note>
          <button type="button" className="erp-btn" onClick={onContinueBlank}>
            Continue and enter the details manually
          </button>
        </div>
      )}

      {/* Already ours — the outcome that prevents a duplicate being created
          thirty fields later, which is the whole reason this gate exists. */}
      {r?.alreadyRegistered && r.existingVoterId && (
        <div className="rounded-md border border-amber-300 bg-amber-50/70 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <UserCheck className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-erp-sm font-medium text-amber-900 dark:text-amber-200">
                This person is already on the register
              </div>
              <p className="text-erp-xs text-amber-800 dark:text-amber-300/90 mt-0.5">
                No external lookup was made. Open the existing record rather than creating a
                second one — a duplicate is far more work to merge than to avoid.
              </p>
              <button
                type="button"
                className="erp-btn mt-2"
                onClick={() => onOpenExisting(r.existingVoterId!)}
              >
                Open the existing voter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Found upstream — offered, not applied. */}
      {r && !r.alreadyRegistered && r.found && r.source === 'ECITIZEN_IPRS' && r.prefill && (
        <PrefillCard r={r} onUse={() => onUsePrefill(r)} onSkip={onContinueBlank} />
      )}

      {/* Nothing known. Two different facts, one action. */}
      {r && !r.alreadyRegistered && !r.found && (
        <div className="space-y-2">
          <div className="text-erp-xs text-muted-foreground inline-flex items-start gap-1.5">
            {r.source === 'NONE' ? (
              <>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span>
                  Nothing was checked upstream — either no identity lookup is configured, it is
                  unavailable, or you do not hold the authority to query it.
                </span>
              </>
            ) : (
              <>
                <Search className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span>Not found on the register or at eCitizen.</span>
              </>
            )}
          </div>
          <button type="button" className="erp-btn erp-btn-primary" onClick={onContinueBlank}>
            Continue and enter the details manually
          </button>
        </div>
      )}

      </div>

      {/* The person in front of you may simply not have their ID. The backend
          accepts a voter without one, so the form must too — otherwise this gate
          quietly removes a class of registration that the register supports. */}
      <div className="pt-1 border-t border-border">
        <button
          type="button"
          className="text-erp-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          disabled={searching}
          onClick={onRegisterWithoutId}
        >
          Register without a national ID
        </button>
        <p className="text-erp-xs text-muted-foreground mt-1">
          Allowed, and sometimes right — but the record cannot be matched against eCitizen or
          de-duplicated by ID later.
        </p>
      </div>
    </div>
  )
}

function PrefillCard({
  r, onUse, onSkip,
}: {
  r: VoterLookupResponse
  onUse: () => void
  onSkip: () => void
}) {
  const p = r.prefill!
  const name = [p.firstName, p.middleName, p.surname].filter(Boolean).join(' ')
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/30 dark:border-emerald-900 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <BadgeCheck className="h-4 w-4 text-emerald-700 dark:text-emerald-400 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-erp-sm font-medium text-emerald-900 dark:text-emerald-200 flex items-center gap-2 flex-wrap">
            Found at eCitizen
            {r.upstreamVerified && (
              <span className="px-1.5 py-0.5 rounded border border-emerald-400/60 text-erp-xs font-normal">
                Government-verified
              </span>
            )}
          </div>
          <dl className="mt-1 text-erp-xs text-emerald-900/90 dark:text-emerald-200/90 space-y-0.5">
            <div><span className="text-emerald-800/70 dark:text-emerald-300/70">Name</span> {name || '—'}</div>
            <div>
              <span className="text-emerald-800/70 dark:text-emerald-300/70">Born</span> {p.birthYear ?? '—'}
              <span className="text-emerald-800/70 dark:text-emerald-300/70 ml-2">Gender</span>{' '}
              {p.genderCode ? p.genderCode.toLowerCase() : '—'}
            </div>
            {p.mobileNumber && (
              <div>
                <span className="text-emerald-800/70 dark:text-emerald-300/70">Mobile</span> {p.mobileNumber}
                {p.mobileVerified && ' (verified)'}
              </div>
            )}
          </dl>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button type="button" className="erp-btn erp-btn-primary" onClick={onUse}>
              Use these details
            </button>
            <button type="button" className="erp-btn" onClick={onSkip}>
              Start blank instead
            </button>
          </div>
          <p className="text-erp-xs text-emerald-800/70 dark:text-emerald-300/60 mt-1.5">
            Every field stays editable. eCitizen returns no address, so the polling station is
            still yours to choose.
          </p>
        </div>
      </div>
    </div>
  )
}
